import { useMemo, useState } from 'react';
import { AdminShell } from '../components/AdminShell';
import { SectionMarker } from '@/components/brand/SectionMarker';
import { Icon } from '@/components/ui/Icon';
import { NeonButton } from '@/components/ui/NeonButton';
import {
  SECRETS_REGISTRY,
  TIER_LABEL,
  TIER_ORDER,
  type SecretSpec,
} from '../secretsRegistry';
import { useSecretsList, callSecret, type SecretRowStatus } from '../useSecretsManager';
import { cn } from '@/lib/cn';

type RowState = 'idle' | 'testing' | 'tested-ok' | 'tested-fail' | 'saving' | 'saved' | 'save-fail';

interface RowUiState {
  value: string;
  state: RowState;
  message?: string;
}

const TARGET_LABEL: Record<SecretSpec['target'], string> = {
  netlify_env:     'Netlify env',
  supabase_secret: 'Supabase secret',
  info:            'Info only',
};

const STATE_TONE: Record<RowState, string> = {
  idle:          'border-white/[0.10]',
  testing:       'border-white/[0.30] bg-charcoal/60',
  'tested-ok':   'border-pink/40 bg-pink/[0.04]',
  'tested-fail': 'border-pink bg-pink/[0.05]',
  saving:        'border-white/[0.30] bg-charcoal/60',
  saved:         'border-pink/60 bg-pink/[0.08] shadow-glow-soft',
  'save-fail':   'border-pink bg-pink/[0.05]',
};

export const AdminSecretsPage = () => {
  const { data, loading, error, refresh } = useSecretsList();
  const [rowStates, setRowStates] = useState<Record<string, RowUiState>>({});
  const [revealed, setRevealed] = useState<Record<string, boolean>>({});
  /** When false (default) we hide rows that already have a value, so the page
   *  only asks for what's missing. The user can flip it to manage existing ones. */
  const [showAll, setShowAll] = useState(false);

  const presence: Record<string, boolean> = useMemo(() => {
    const m: Record<string, boolean> = {};
    (data?.rows ?? []).forEach((r: SecretRowStatus) => (m[r.key] = r.present));
    return m;
  }, [data]);

  const update = (key: string, patch: Partial<RowUiState>) =>
    setRowStates((prev) => ({ ...prev, [key]: { ...(prev[key] ?? { value: '', state: 'idle' }), ...patch } }));

  const handleTest = async (key: string) => {
    const current = rowStates[key]?.value ?? '';
    update(key, { state: 'testing', message: undefined });
    const result = await callSecret('test', key, current);
    update(key, {
      state: result.ok ? 'tested-ok' : 'tested-fail',
      message: result.message,
    });
  };

  const handleSave = async (key: string) => {
    const current = rowStates[key]?.value ?? '';
    update(key, { state: 'saving', message: undefined });
    const result = await callSecret('save', key, current);
    if (result.ok) {
      update(key, { state: 'saved', message: result.message });
      // Refresh presence map after a short delay so the dot turns green.
      window.setTimeout(() => refresh(), 1500);
    } else {
      update(key, { state: 'save-fail', message: result.message });
    }
  };

  const bootstrapMissing = data && (
    !data.bootstrap.netlifyToken || !data.bootstrap.netlifySiteId ||
    !data.bootstrap.mgmtToken || !data.bootstrap.projectRef
  );

  // A row is "needed" if it isn't already set. Info rows (webhook URLs) are
  // always shown — they're not secrets, they're instructions. Bootstrap rows
  // are also always shown when bootstrap is incomplete, so the banner makes sense.
  const isNeeded = (spec: SecretSpec): boolean => {
    if (spec.target === 'info') return true;
    return !presence[spec.key];
  };

  const visibleRowsFor = (items: SecretSpec[]): SecretSpec[] =>
    showAll ? items : items.filter(isNeeded);

  const totalRows = SECRETS_REGISTRY.filter((s) => s.target !== 'info').length;
  const setRows = SECRETS_REGISTRY.filter((s) => s.target !== 'info' && presence[s.key]).length;
  const missingRows = totalRows - setRows;

  const grouped = TIER_ORDER.map((tier) => ({
    tier,
    items: visibleRowsFor(SECRETS_REGISTRY.filter((s) => s.tier === tier)),
  })).filter((g) => g.items.length > 0);

  return (
    <AdminShell eyebrow="Moderation · secrets">
      <div className="px-5 py-7 md:px-12 md:py-10">
        <SectionMarker className="mb-3">Configuration · secrets</SectionMarker>
        <h1 className="font-display text-3xl font-black uppercase tracking-display text-text-primary md:text-4xl">
          One place<span className="text-pink">.</span>{' '}
          <span className="font-light italic text-text-secondary">every key</span>
          <span className="text-pink">.</span>
        </h1>
        <p className="mt-3 max-w-3xl text-xs text-text-secondary">
          Each row tests its value live, then saves it to wherever it needs to live —
          Netlify env (for client-bundled <code className="font-mono">VITE_*</code> vars) or
          Supabase function secrets (for server-only keys). Only the keys still missing
          are shown by default, so you never paste the same value twice.
        </p>

        {data && (
          <div className="mt-5 flex flex-wrap items-center gap-3 border-y border-white/[0.06] py-3 font-display text-[10px] font-bold uppercase tracking-widest">
            <span className="text-text-muted">
              <span className="text-pink">{setRows}</span> of {totalRows} set
            </span>
            <span className="text-text-muted">·</span>
            <span className={cn(missingRows === 0 ? 'text-pink' : 'text-text-primary')}>
              {missingRows === 0 ? 'all configured' : `${missingRows} still needed`}
            </span>
            <span className="ml-auto flex items-center gap-3">
              {missingRows === 0 && !showAll && (
                <span className="text-pink">Nothing to ask for. Toggle below to manage existing.</span>
              )}
              <button
                type="button"
                onClick={() => setShowAll((v) => !v)}
                className={cn(
                  'border px-3 py-1 transition-colors duration-fast ease-signal',
                  showAll
                    ? 'border-pink text-pink'
                    : 'border-white/[0.15] text-text-muted hover:border-white/[0.30] hover:text-text-primary',
                )}
              >
                {showAll ? 'Hide already-set' : 'Show already-set'}
              </button>
            </span>
          </div>
        )}

        {error && (
          <p className="mt-4 font-display text-[10px] font-bold uppercase tracking-widest text-pink">
            — {error}
          </p>
        )}

        {loading && !data && (
          <p className="mt-6 font-display text-[10px] font-bold uppercase tracking-widest text-text-muted">
            Loading<span className="text-pink">.</span>
          </p>
        )}

        {bootstrapMissing && (
          <div className="mt-6 border border-pink/40 bg-pink/[0.06] p-5">
            <p className="font-display text-[10px] font-bold uppercase tracking-widest text-pink">
              Bootstrap required
            </p>
            <p className="mt-2 text-xs text-text-secondary">
              Set the four <code className="font-mono">admin-bootstrap</code> secrets manually, ONCE, via:
            </p>
            <pre className="mt-3 overflow-x-auto rounded bg-charcoal/60 p-3 font-mono text-[11px] text-text-primary">
{`supabase secrets set NETLIFY_API_TOKEN=... NETLIFY_SITE_ID=... \\
  SUPABASE_MANAGEMENT_ACCESS_TOKEN=sbp_... SUPABASE_PROJECT_REF=... \\
  --project-ref <your-project-ref>`}
            </pre>
            <p className="mt-3 text-xs text-text-secondary">
              After that, every other row on this page can save itself — to Netlify or Supabase as required.
            </p>
          </div>
        )}

        {data && grouped.map(({ tier, items }) => (
          <section key={tier} className="mt-10">
            <SectionMarker className="mb-3">{TIER_LABEL[tier]}</SectionMarker>
            <ul className="space-y-3">
              {items.map((spec) => {
                const ui = rowStates[spec.key] ?? { value: '', state: 'idle' as RowState };
                const present = presence[spec.key];
                const tone = STATE_TONE[ui.state] ?? 'border-white/[0.10]';
                const isInfo = spec.target === 'info';
                return (
                  <li key={spec.key} className={cn('border bg-charcoal/40 p-5 transition-colors duration-fast ease-signal', tone)}>
                    <div className="flex flex-wrap items-baseline justify-between gap-3">
                      <div className="min-w-0">
                        <p className="font-mono text-sm font-bold text-text-primary">
                          {spec.key}
                        </p>
                        <p className="mt-1 flex flex-wrap items-center gap-2 font-display text-[10px] font-bold uppercase tracking-widest text-text-muted">
                          <span>{TARGET_LABEL[spec.target]}</span>
                          {!isInfo && (
                            <span className={cn(
                              'inline-flex items-center gap-1',
                              present ? 'text-pink' : 'text-text-muted',
                            )}>
                              <span className={cn('h-1.5 w-1.5 rounded-full', present ? 'bg-pink shadow-glow-soft' : 'bg-white/20')} />
                              {present ? 'set' : 'unset'}
                            </span>
                          )}
                          {spec.docsUrl && (
                            <a
                              href={spec.docsUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-pink hover:underline"
                            >
                              docs ↗
                            </a>
                          )}
                        </p>
                      </div>
                    </div>

                    <p className="mt-3 text-xs text-text-secondary">{spec.description}</p>

                    {isInfo ? (
                      <div className="mt-3 flex items-center gap-3">
                        <code className="flex-1 break-all rounded bg-ink/60 px-3 py-2 font-mono text-[11px] text-text-primary">
                          {spec.staticValue}
                        </code>
                        <button
                          type="button"
                          onClick={() => navigator.clipboard?.writeText(spec.staticValue ?? '')}
                          className="font-display text-[10px] font-bold uppercase tracking-widest text-pink hover:underline"
                        >
                          Copy
                        </button>
                      </div>
                    ) : (
                      <>
                        <div className="mt-4 flex flex-wrap items-stretch gap-3">
                          <div className="relative min-w-0 flex-1">
                            <input
                              type={revealed[spec.key] ? 'text' : 'password'}
                              value={ui.value}
                              onChange={(e) => update(spec.key, { value: e.target.value, state: 'idle', message: undefined })}
                              placeholder={present ? '•••••• (set — paste a new value to overwrite)' : 'paste value here'}
                              autoComplete="off"
                              spellCheck={false}
                              className={cn(
                                'w-full border border-white/[0.10] bg-ink/60 px-3 py-2 font-mono text-sm',
                                'placeholder:text-text-muted focus:border-pink focus:outline-none',
                              )}
                            />
                            <button
                              type="button"
                              onClick={() => setRevealed((r) => ({ ...r, [spec.key]: !r[spec.key] }))}
                              className="absolute right-2 top-1/2 -translate-y-1/2 text-text-muted hover:text-pink"
                              aria-label={revealed[spec.key] ? 'Hide value' : 'Reveal value'}
                            >
                              <Icon name={revealed[spec.key] ? 'visibility_off' : 'visibility'} size={16} />
                            </button>
                          </div>
                          <NeonButton
                            type="button"
                            size="md"
                            variant="outline"
                            disabled={!ui.value || ui.state === 'testing' || ui.state === 'saving'}
                            onClick={() => handleTest(spec.key)}
                            trailing={<Icon name="play_arrow" size={14} />}
                          >
                            {ui.state === 'testing' ? 'Testing' : 'Test'}
                          </NeonButton>
                          <NeonButton
                            type="button"
                            size="md"
                            disabled={!ui.value || ui.state === 'saving' || ui.state === 'testing'}
                            onClick={() => handleSave(spec.key)}
                            trailing={<Icon name={ui.state === 'saved' ? 'check' : 'arrow_forward'} size={14} />}
                          >
                            {ui.state === 'saving' ? 'Saving' : ui.state === 'saved' ? 'Saved' : 'Save'}
                          </NeonButton>
                        </div>
                        {ui.message && (
                          <p className={cn(
                            'mt-3 font-display text-[10px] font-bold uppercase tracking-widest',
                            ui.state === 'saved' || ui.state === 'tested-ok' ? 'text-pink' : 'text-text-muted',
                          )}>
                            — {ui.message}
                          </p>
                        )}
                      </>
                    )}
                  </li>
                );
              })}
            </ul>
          </section>
        ))}
      </div>
    </AdminShell>
  );
};
